import * as forge from 'node-forge'
import Storage from './LocalForageStorage'
import { IStorage } from './IStorage'

declare var KJUR: any

const { pki, util, asn1, pkcs12 } = forge

export enum Algorithm {
  SHA256 = 'sha256',
  SHA384 = 'sha384',
  SHA512 = 'sha512'
}

export { Storage, IStorage }

/**
 * Class has functions to upload and download a certificate to and from 
 * browser storage as well as perform document signing
 * 
 * @export
 * @class CertificateUtils
 */
export class CertificateUtils {
  private storage: IStorage
  private certStoreKey: string

  /**
   * Creates an instance of CertificateUtils.
   * 
   * @param {(IStorage | string)} storageOrName - the name of the underlying browser storage 
   * to create or a object that implements the IStorage interface
   * @param {string} certStoreKey - the key to use when adding a certificate to storage.
   * Id of user to which to certificate belongs, will be appended to this value to create 
   * the final key
   * 
   * @memberOf CertificateUtils
   */
  constructor(storageOrName: IStorage | string, certStoreKey: string) {
    if (!storageOrName) {
      throw new Error(
        'Either the storage name or an an object that implements IStorage instance must be specified'
      )
    }
    if (!certStoreKey) {
      throw new Error('The store key must be specified')
    }

    if (typeof storageOrName === 'string') {
      this.storage = new Storage(storageOrName)
    } else {
      this.storage = storageOrName
    }

    this.certStoreKey = certStoreKey
  }

  /**
   * Stores a certificate file in the browser storage
   * 
   * @param {File} file - The PKCS12 file uploaded via a file input element
   * @param {string} userId - the id of the user to which the certificate belongs
   * @returns {Promise<Uint8Array>} - A promise of the certificate file as an Uint8Array
   * 
   * @memberOf CertificateUtils
   */
  public storePkcs12File(file: File, userId: string): Promise<Uint8Array> {
    if (!file) {
      return Promise.reject<Uint8Array>(
        'The certificate file must be specified'
      )
    }
    if (!userId) {
      return Promise.reject<Uint8Array>('The user ID must be specified')
    }

    return new Promise<Uint8Array>((resolve, reject) => {
      let p12FileReader = new FileReader()

      /**
       * Handler that saved the uploaded file to the browser's storage
       * 
       * @param {any} event
       */
      p12FileReader.onload = event => {
        let p12File = new Uint8Array((<FileReader>event.target).result)

        this.storage
          .set(this.getStoreKey(userId), p12File)
          .then(_ => resolve(p12File))
      }

      p12FileReader.readAsArrayBuffer(file)
    })
  }

  /**
   * Determines if a PKCS12 file belonging to a user is stored in browser storage
   * 
   * @param {string} userId - the id of the user to which the certificate belongs
   * @returns {Promise<boolean>} - A promise of the boolean value. True if stored, otherwise false. 
   * 
   * @memberOf CertificateUtils
   */
  public async isPkcs12FileStored(userId: string): Promise<boolean> {
    if (!userId) {
      return Promise.reject<boolean>('The user ID must be specified')
    }

    const p12File = await this.getPkcs12FileFromStore(userId)

    return !!p12File
  }

  /**
   * Downloads a PKCS12 file from browser storage
   * 
   * @param {string} userId - the id of the user to which the certificate belongs
   * @param {string} fileName - the name to give the downloaded certificate file. 
   * Defaults to the userId
   * @param {string} extension - The file extension. Defaults to 'pfx'
   * 
   * @memberOf CertificateUtils
   */
  public async downloadPkcs12File(
    userId: string,
    fileName?: string,
    extension: string = 'pfx'
  ) {
    if (!userId) {
      throw new Error('The user ID must be specified')
    }

    fileName = fileName || userId

    const p12File = await this.getPkcs12FileFromStore(userId)

    const certFileBlob = new Blob([p12File])
    let url = URL.createObjectURL(certFileBlob)
    const pfxFileName = `${fileName}.${extension}`

    let a = document.createElement('a')
    document.body.appendChild(a)
    a.style.display = 'none'
    a.href = url
    a.setAttribute('download', pfxFileName)
    a.click()
    URL.revokeObjectURL(url)
  }

  /**
   * Signs the given document
   * 
   * @param {(Uint8Array | string)} doc - The file byte array or string 
   * @param {string} userId - The id of the user to which the certificate belongs
   * @param {string} privateKeyPassword - The password for the private key associated with the certificate   
   * @param {SignatureType} [signatureType=SignatureType.CAdES]  - Whether or not to wrap the signature in a PKCS#7 
   * message format
   * @param {Date} timeStamp - The date that will be embedded in the PKCS#7/CMS message or a timestamp token
   * from a TimeStamp server to be added to the CAdES message
   * @param {Algorithm} [algorithm=Algorithm.SHA512]  - The message digest algorithm. Default is sha512
   * @returns {Promise<string>} - A promise of the signed document value. 
   * @memberOf CertificateUtils
   */
  public async signDocument(
    doc: Uint8Array | string,
    userId: string,
    privateKeyPassword: string,
    timeStamp?: Date,
    algorithm: Algorithm = Algorithm.SHA512
  ): Promise<string> {
    if (!doc) {
      Promise.reject<string>('The document to sign must be specified')
    }
    if (!userId) {
      Promise.reject<string>('The user ID must be specified')
    }
    if (!privateKeyPassword) {
      Promise.reject<string>('Certificate password must be specified')
    }

    const p12File = await this.getPkcs12FileFromStore(userId)

    if (!p12File) {
      throw new Error(`No certificate found for user ID: ${userId}`)
    }

    let { key, cert } = this.getPrivateKeyAndCert(p12File, privateKeyPassword)

    if (!(doc instanceof Uint8Array || typeof doc === 'string')) {
      throw new Error('The document to sign must be a string or a Uint8Array')
    }

    return this.getCAdESSignedPackage(doc, cert, key, algorithm, timeStamp)
  }

  /**
   * Signs a PDF document and embeds the signature
   * 
   * 
   * @memberOf CertificateUtils
   */
  public signPdf() {
    throw new Error('signPdf is not yet implemented')
    // TODO: Call PDFSign to sign the document
    // TODO:
    // 1. Patch pdfsign.js (pdfsign-patched-dci.js) to call a custom signDetached function
    //    instead of the patched function from forge-patched.js file
    // 2. Create signedDetachedFunction to take a 'msg' parameter that has the same functionality
    //    as https://github.com/digitalbazaar/forge/blob/master/lib/pkcs7.js#L330 but patched as in
    //    https://github.com/Communication-Systems-Group/pdfsign.js/blob/master/src/lib/pkcs7-detached.js#L350
    // 3. Call PDFSign to sign document. See sample in samples/pdf-sign.html
  }

  /**
   * Gets the certificate from the store
   * 
   * @param {string} userId - The id of the user to which the certificate belongs
   * @returns - The certificate file bytes
   * 
   * @memberOf CertificateUtils
   */
  public getPkcs12FileFromStore(userId: string) {
    return this.storage.get<Uint8Array>(this.getStoreKey(userId))
  }

  private getCAdESSignedPackage(
    data: Uint8Array | string,
    cert: any,
    privateKey: any,
    algorithm: Algorithm,
    timeStamp?: Date
  ) {
    return this.createCmsSignedPackage(
      data,
      cert,
      privateKey,
      algorithm,
      timeStamp
    )
  }

  private createCmsSignedPackage(
    data: Uint8Array | string,
    cert: any,
    privateKey: any,
    algorithm: Algorithm,
    timeStamp?: Date
  ) {
    const privateKeyPem = pki.privateKeyToPem(privateKey)
    const certPem = pki.certificateToPem(cert)

    const signedAttr: any = { SigningCertificateV2: { array: [certPem] } }

    signedAttr.SigningTime = timeStamp
      ? { str: this.toDateString(timeStamp) }
      : {}

    let cms = KJUR.asn1.cms.CMSUtil.newSignedData({
      content:
        data instanceof Uint8Array
          ? { hex: util.binary.hex.encode(data) }
          : { str: data },
      certs: [certPem],
      signerInfos: [
        {
          hashAlg: algorithm,
          sAttr: signedAttr,
          signerCert: certPem,
          sigAlg: `${algorithm.toUpperCase()}withRSA`,
          signerPrvKey: privateKeyPem
        }
      ]
    })

    const cmsHex = cms.getContentInfoEncodedHex()

    return util.binary.base64.encode(util.binary.hex.decode(cmsHex))
  }

  private getPrivateKeyAndCert(p12File: Uint8Array, certPassword: string) {
    // create forge byte bugger
    let buffer = new util.ByteBuffer(p12File.buffer)
    // get forge cert from byte buffer
    let p12Asn1 = asn1.fromDer(buffer)
    // decrypt p12 using non-strict parsing mode (resolves some ASN.1 parse errors)
    let p12Cert = pkcs12.pkcs12FromAsn1(p12Asn1, false, certPassword)

    // get key bags
    let keyBags = p12Cert.getBags({ bagType: pki.oids.pkcs8ShroudedKeyBag })
    // get key
    let keyBag = keyBags[pki.oids.pkcs8ShroudedKeyBag][0]

    if (!keyBag) {
      throw new Error('PKCS12 file does not contain a private key')
    }

    let key = keyBag.key
    // if the key is in a format unrecognized by forge then
    // bag.key will be `null`, use bag.asn1 to get the ASN.1
    // representation of the key
    if (key === null) {
      let keyAsn1 = keyBag.asn1
      key = pki.privateKeyFromAsn1(keyAsn1)

      if (!key) {
        throw new Error('Unable to read key stored in PKCS12 file')
      }
    }

    // get cert bags
    let certBags = p12Cert.getBags({ bagType: pki.oids.certBag })
    // get cert
    let certBag = certBags[pki.oids.certBag][0]

    if (!certBag) {
      throw new Error('PKCS12 file does not contain a certificate')
    }

    if (!certBag.cert) {
      throw new Error('Unable to read certificate stored in PKCS12 file')
    }

    let cert = certBag.cert

    return { key, cert }
  }

  private getStoreKey(userId: string) {
    return `${this.certStoreKey}_${userId}`
  }

  private toDateString(date: Date) {
    const year = date.getUTCFullYear().toString().substr(2)

    let month = (date.getUTCMonth() + 1).toString()
    if (month.length === 1) {
      month = '0' + month
    }

    let day = date.getUTCDate().toPrecision()
    if (day.length === 1) {
      day = '0' + day
    }

    let hour = date.getUTCHours().toString()
    if (hour.length === 1) {
      hour = '0' + hour
    }

    let min = date.getUTCMinutes().toString()
    if (min.length === 1) {
      min = '0' + min
    }

    let sec = date.getUTCSeconds().toString()
    if (sec.length === 1) {
      sec = '0' + sec
    }

    return year + month + day + hour + min + sec + 'Z'
  }
}



// WEBPACK FOOTER //
// ./src/CertificateUtils.ts